import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type * as React from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography
} from "@mui/material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import AccountExitLink from "components/v3/sharedComponents/AccountExitLink";
import GLButton from "components/v3/sharedComponents/GLButton";
import GLEmailField from "components/v3/forms/formComponents/GLEmailField";
import GLPhoneField from "components/v3/forms/formComponents/GLPhoneField";
import GLSelect from "components/v3/forms/formComponents/GLSelect";
import GLTextField from "components/v3/forms/formComponents/GLTextField";
import {
  type FormFieldContents,
  useFormFieldState
} from "components/v3/forms/formComponents/FormUtils";
import { legalNameTooltip } from "components/v3/forms/formComponents/userInfoTooltips";
import { COLORS } from "components/v3/Theme/colors";
import { PATHS } from "constants/pathConstants";
import {
  buildSalesforceCaptchaSettingsJson,
  CANCEL_REFUND_TARGET_OPTIONS,
  getCancelReasonOptions,
  salesforceWebToCaseConfig,
  salesforceWebToCaseFieldIds
} from "constants/salesforceWebToCaseConfig";
import {
  selectUserPersonalInformation,
  type UserPersonalInformation
} from "store/reducers/user/UserAccountSlice";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import PageTitle from "utils/PageTitle";

const WEB_TO_CASE_IFRAME_NAME = "gl-web-to-case-iframe";
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";
const RECAPTCHA_SCRIPT_SRC = "https://www.google.com/recaptcha/api.js";

const { refundTarget: refundTargetFieldId, giftRecipient: giftRecipientFieldId, cancelReason: cancelReasonFieldId } =
  salesforceWebToCaseFieldIds;

interface CancelMembershipFormFields {
  [key: string]: FormFieldContents;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const getStringFormValue = (
  fields: CancelMembershipFormFields,
  fieldId: string
): string => {
  const value = fields[fieldId];
  return typeof value === "string" ? value : "";
};

type GoogleRecaptcha = {
  getResponse: (widgetId?: number) => string;
  render: (
    container: HTMLElement | string,
    params: {
      sitekey: string;
      callback?: () => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    }
  ) => number;
  reset: (widgetId?: number) => void;
};

declare global {
  // eslint-disable-next-line no-var
  var grecaptcha: GoogleRecaptcha | undefined;
  // eslint-disable-next-line no-var
  var __glOnRecaptchaLoaded: (() => void) | undefined;
}

const sectionCardSx = {
  bgcolor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box",
  p: { sm: 3, xs: 2.5 },
  width: "100%"
} as const;

const inlineLinkSx = {
  color: COLORS.DARK_GRAY,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": { color: COLORS.PRIMARY_DARK }
} as const;

const buildInitialState = (
  user: UserPersonalInformation
): CancelMembershipFormFields => ({
  firstName: user.firstName ?? "",
  lastName: user.lastName ?? "",
  email: user.email ?? "",
  phone: user.phone ?? "",
  [refundTargetFieldId]: "",
  [giftRecipientFieldId]: "",
  [cancelReasonFieldId]: ""
});

const CancelMembershipPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userInformation = useSelector(selectUserPersonalInformation);
  const formRef = useRef<HTMLFormElement | null>(null);
  const awaitingWebToCaseRef = useRef(false);
  const webToCaseWatchdogRef = useRef<number | null>(null);
  const captchaContainerRef = useRef<HTMLDivElement | null>(null);
  const captchaSettingsRef = useRef<HTMLInputElement | null>(null);
  const captchaWidgetRendered = useRef(false);
  const captchaWidgetIdRef = useRef<number | null>(null);
  const [confirmedCancellation, setConfirmedCancellation] = useState(false);
  const [captchaSolved, setCaptchaSolved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const writeCaptchaSettingsTimestamp = useCallback(() => {
    const settingsInput = captchaSettingsRef.current;
    if (!settingsInput) return;
    try {
      const elems = JSON.parse(settingsInput.value) as Record<string, unknown>;
      elems.ts = JSON.stringify(Date.now());
      settingsInput.value = JSON.stringify(elems);
    } catch {
      // If the hidden input has been clobbered we just skip this tick.
    }
  }, []);

  const initialFormState = useMemo(
    () => buildInitialState(userInformation),
    [userInformation]
  );

  const cancelReasonOptions = useMemo(() => getCancelReasonOptions(), []);

  const initialCaptchaSettings = useMemo(
    () => buildSalesforceCaptchaSettingsJson(),
    []
  );

  const { formFields, formStatus, handleInputChange } =
    useFormFieldState<CancelMembershipFormFields>(initialFormState);

  const useRecaptcha = salesforceWebToCaseConfig.useRecaptcha;
  const recaptchaSiteKey = salesforceWebToCaseConfig.recaptchaSiteKey;

  // Salesforce must allow this origin for `retURL`; each deploy (e.g. member.test vs prod)
  // picks up the correct URL automatically. Localhost is usually blocked server-side.
  const webToCaseRetUrl = useMemo(
    () => `${globalThis.window.location.origin}/web-to-case-submitted.html`,
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== globalThis.window.location.origin) return;
      const data = event.data as { type?: string } | null;
      if (data?.type !== "gl-web-to-case-submitted") return;
      if (!awaitingWebToCaseRef.current) return;
      awaitingWebToCaseRef.current = false;
      if (webToCaseWatchdogRef.current != null) {
        globalThis.window.clearTimeout(webToCaseWatchdogRef.current);
        webToCaseWatchdogRef.current = null;
      }
      const form = formRef.current;
      if (form) {
        form.removeAttribute("target");
      }
      setSubmitting(false);
      dispatch(
        openGLSnackbar({
          variant: "success",
          header: "",
          subText:
            "Your cancellation and refund request was submitted. We may reach out to confirm your information."
        })
      );
      history.push(PATHS.ACCOUNT_MANAGEMENT);
    };
    globalThis.window.addEventListener("message", onMessage);
    return () => {
      globalThis.window.removeEventListener("message", onMessage);
    };
  }, [dispatch, history]);

  useEffect(() => {
    return () => {
      if (webToCaseWatchdogRef.current != null) {
        globalThis.window.clearTimeout(webToCaseWatchdogRef.current);
        webToCaseWatchdogRef.current = null;
      }
    };
  }, []);

  PageTitle("Request Cancellation and Refund");

  useEffect(() => {
    if (!useRecaptcha || !recaptchaSiteKey) return;

    const resetWidgetState = () => {
      const wid = captchaWidgetIdRef.current;
      if (wid != null && globalThis.grecaptcha?.reset) {
        try {
          globalThis.grecaptcha.reset(wid);
        } catch {
        }
      }
      captchaWidgetIdRef.current = null;
      captchaWidgetRendered.current = false;
      setCaptchaSolved(false);
    };

    const renderWidget = () => {
      if (captchaWidgetRendered.current) return;
      const container = captchaContainerRef.current;
      const grecaptcha = globalThis.grecaptcha;
      if (!container || !grecaptcha?.render) return;
      try {
        const id = grecaptcha.render(container, {
          sitekey: recaptchaSiteKey,
          callback: () => {
            setCaptchaSolved(true);
          },
          "expired-callback": () => {
            setCaptchaSolved(false);
          }
        });
        captchaWidgetIdRef.current = id;
        captchaWidgetRendered.current = true;
      } catch {}
    };

    if (globalThis.grecaptcha?.render) {
      renderWidget();
      return () => {
        resetWidgetState();
      };
    }

    globalThis.__glOnRecaptchaLoaded = renderWidget;

    if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `${RECAPTCHA_SCRIPT_SRC}?onload=__glOnRecaptchaLoaded&render=explicit`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      return () => {
        globalThis.__glOnRecaptchaLoaded = undefined;
        resetWidgetState();
      };
    }

    const pollHandle = globalThis.setInterval(() => {
      if (globalThis.grecaptcha?.render) {
        renderWidget();
        globalThis.clearInterval(pollHandle);
      }
    }, 200);
    return () => {
      globalThis.clearInterval(pollHandle);
      globalThis.__glOnRecaptchaLoaded = undefined;
      resetWidgetState();
    };
  }, [recaptchaSiteKey, useRecaptcha]);

  useEffect(() => {
    if (!useRecaptcha) return;
    const handle = globalThis.setInterval(() => {
      // Salesforce expects a non-empty `ts` in captcha_settings; we must keep
      // updating it even after the user solves reCAPTCHA (the old logic skipped
      // updates whenever g-recaptcha-response was set, leaving ts as "").
      writeCaptchaSettingsTimestamp();
    }, 500);
    return () => globalThis.clearInterval(handle);
  }, [useRecaptcha, writeCaptchaSettingsTimestamp]);

  const refundTarget = getStringFormValue(formFields, refundTargetFieldId);
  const giftRecipient = getStringFormValue(formFields, giftRecipientFieldId);
  const cancelReason = getStringFormValue(formFields, cancelReasonFieldId);

  const isGiftCancellation = refundTarget === "A gift purchase";

  const requiredFieldsFilled = Boolean(
    formFields.firstName &&
    formFields.lastName &&
    formFields.email &&
    formFields.phone &&
    refundTarget &&
    cancelReason &&
    (!isGiftCancellation || giftRecipient)
  );

  const relevantInvalidFields = isGiftCancellation
    ? formStatus.invalidFields
    : formStatus.invalidFields.filter((f) => f !== giftRecipientFieldId);
  const noFieldErrors = relevantInvalidFields.length === 0;

  const canSubmit =
    salesforceWebToCaseConfig.isConfigured &&
    confirmedCancellation &&
    requiredFieldsFilled &&
    noFieldErrors &&
    (!useRecaptcha || captchaSolved);

  const handleExit = () => {
    history.push(PATHS.ACCOUNT_MANAGEMENT);
  };

  const handleSubmitClick = () => {
    if (!canSubmit || submitting) return;
    const form = formRef.current;
    if (!form) return;
    if (useRecaptcha) {
      const wid = captchaWidgetIdRef.current;
      const gr = globalThis.grecaptcha;
      const token =
        wid != null && gr?.getResponse ? gr.getResponse(wid).trim() : "";
      if (!token) {
        return;
      }
      writeCaptchaSettingsTimestamp();
    }

    if (webToCaseWatchdogRef.current != null) {
      globalThis.window.clearTimeout(webToCaseWatchdogRef.current);
      webToCaseWatchdogRef.current = null;
    }

    awaitingWebToCaseRef.current = true;
    setSubmitting(true);
    form.target = WEB_TO_CASE_IFRAME_NAME;
    try {
      form.submit();
    } catch {
      awaitingWebToCaseRef.current = false;
      form.removeAttribute("target");
      setSubmitting(false);
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText:
            "We could not submit your request from this page. Please try again or contact support."
        })
      );
      return;
    }

    webToCaseWatchdogRef.current = globalThis.window.setTimeout(() => {
      webToCaseWatchdogRef.current = null;
      if (!awaitingWebToCaseRef.current) return;
      awaitingWebToCaseRef.current = false;
      form.removeAttribute("target");
      setSubmitting(false);
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText:
            "We did not get confirmation that your request finished submitting. If you are unsure whether it went through, try again or contact support."
        })
      );
    }, 45_000);
  };

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          alignItems: "stretch",
          alignSelf: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 740, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <AccountExitLink to={PATHS.ACCOUNT_MANAGEMENT} />

        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          Request Cancellation and Refund
        </Typography>

        <Box sx={sectionCardSx}>
          <Typography variant="body" sx={{ mb: 2 }}>
            You have the right to cancel your Direct Membership for a full
            refund within 72 hours of purchase, so long as no services
            (including genetic testing services) were used between the purchase
            and the cancellation. This applies to memberships you purchased for
            yourself and gift memberships you purchased for others. Learn more
            about your options in our{" "}
            <Box
              component={RouterLink}
              rel="noopener noreferrer"
              target="_blank"
              to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
              sx={inlineLinkSx}
            >
              Terms of Use
            </Box>
            .
          </Typography>
          <Typography variant="body" sx={{ m: 0 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              To request a cancellation and refund, submit the form below.
            </Box>{" "}
            Please note that we may reach out to confirm the information
            provided here.
          </Typography>

          <Divider
            sx={{
              borderColor: COLORS.PRIMARY_LIGHTER,
              my: 3
            }}
          />

          <Box
            component="form"
            ref={formRef}
            action={salesforceWebToCaseConfig.formAction}
            method="POST"
            encType="application/x-www-form-urlencoded"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              width: "100%",
              "& .MuiOutlinedInput-root:not(.Mui-disabled)": {
                backgroundColor: COLORS.WHITE
              }
            }}
          >
            {useRecaptcha ? (
              <input
                ref={captchaSettingsRef}
                type="hidden"
                name="captcha_settings"
                defaultValue={initialCaptchaSettings}
              />
            ) : null}
            <input
              type="hidden"
              name="orgid"
              value={salesforceWebToCaseConfig.orgId}
              readOnly
            />
            <input
              type="hidden"
              name="retURL"
              value={webToCaseRetUrl}
              readOnly
            />
            <input
              type="hidden"
              name="recordType"
              value={salesforceWebToCaseConfig.recordTypeId}
              readOnly
            />
            <input type="hidden" name="external" value="1" readOnly />

            <input
              type="hidden"
              name="name"
              value={`${formFields.firstName} ${formFields.lastName}`.trim()}
              readOnly
            />

            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: { sm: "1fr 1fr", xs: "1fr" }
              }}
            >
              <GLTextField
                disabled
                required
                id="firstName"
                label="First Name"
                value={formFields.firstName}
                tooltip={legalNameTooltip}
              />
              <GLTextField
                disabled
                required
                id="lastName"
                label="Last Name"
                value={formFields.lastName}
                tooltip={legalNameTooltip}
              />
            </Box>

            <GLEmailField
              id="email"
              label="Contact Email Address"
              required
              value={formFields.email}
              placeholder="name@example.com"
              onChange={handleInputChange}
            />

            <GLPhoneField
              id="phone"
              label="Contact Phone Number"
              required
              value={formFields.phone}
              placeholder="(555) 555-5555"
              onChange={handleInputChange}
            />

            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: { sm: "1fr 1fr", xs: "1fr" }
              }}
            >
              <GLSelect
                id={refundTargetFieldId}
                label="I am requesting cancellation/refund for"
                required
                value={refundTarget}
                options={[...CANCEL_REFUND_TARGET_OPTIONS]}
                onChange={handleInputChange}
              />
              {isGiftCancellation ? (
                <GLTextField
                  required
                  id={giftRecipientFieldId}
                  label="Name of gift recipient"
                  value={giftRecipient}
                  placeholder="Recipient's name"
                  onChange={handleInputChange}
                />
              ) : (
                <Box />
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: { sm: "1fr 1fr", xs: "1fr" }
              }}
            >
              <GLSelect
                id={cancelReasonFieldId}
                label="Reason for canceling"
                required
                value={cancelReason}
                options={cancelReasonOptions}
                onChange={handleInputChange}
              />
              <Box />
            </Box>

            <FormControlLabel
              data-testid="cancel-confirm-checkbox"
              control={
                <Checkbox
                  checked={confirmedCancellation}
                  onChange={(e) => setConfirmedCancellation(e.target.checked)}
                  sx={{ alignSelf: "flex-start", mt: -1.2 }}
                />
              }
              label={
                <Typography variant="body_strong">
                  I confirm that I would like to cancel this membership per the{" "}
                  <Box
                    component={RouterLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={inlineLinkSx}
                  >
                    Terms of Use
                  </Box>
                  .
                </Typography>
              }
              sx={{
                alignItems: "flex-start",
                gap: 1,
                m: 0,
                "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 }
              }}
            />

            {useRecaptcha ? (
              <Box
                ref={captchaContainerRef}
                sx={{
                  display: "flex",
                  justifyContent: { sm: "flex-start", xs: "center" },
                  minHeight: 78,
                  minWidth: { xs: "100%", sm: 304 },
                  overflow: "visible",
                  width: "100%"
                }}
              />
            ) : null}
            <Box
              component="iframe"
              name={WEB_TO_CASE_IFRAME_NAME}
              title="Salesforce Web-to-Case submission"
              sx={{
                border: 0,
                display: "none",
                height: 0,
                width: 0
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            alignItems: { sm: "center", xs: "stretch" },
            display: "flex",
            flexDirection: { sm: "row", xs: "column" },
            gap: 2,
            justifyContent: "center"
          }}
        >
          <GLButton
            buttonType="primary"
            disabled={!canSubmit}
            loading={submitting}
            onClick={handleSubmitClick}
            text="Request Cancellation and Refund"
            sx={{
              maxWidth: { sm: "none", xs: "100%" },
              width: { sm: "auto", xs: "100%" }
            }}
          />
          <GLButton
            buttonType="secondary"
            onClick={handleExit}
            text="Nevermind"
            sx={{
              maxWidth: { sm: "none", xs: "100%" },
              width: { sm: "auto", xs: "100%" }
            }}
          />
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default CancelMembershipPage;
